import {
  Box,
  Button,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { usePrivy } from '@privy-io/react-auth';
import { config } from '../../../shared/constants/WagmiConfig';
import React, { useEffect, useMemo, useState } from 'react';
import LoadingButton from '../../../components/common/LoadingButton';
import { ethers, BigNumberish } from 'ethers';
import VirtualBalanceChip from '../../../components/common/VirtualBalanceChip';
import { virtualDetailModalStyle } from '../../../components/virtual/VirtualDetailModal.style';
import { enqueueSnackbar } from 'notistack';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { activeChain, checkNetwork } from '../../../shared/utils/CheckNetwork';
import { renderContractError } from '../../../shared/utils/RenderContractError';
import { formatNumber } from '../../../shared/utils/Formatter';
import { virtualTokenABI } from '../../../assets/abis/virtualToken';
import { readContract, waitForTransactionReceipt } from 'wagmi/actions';
import { useGetCoinData } from '../../../api/coingecko/queries';
import useStakedList from '../../../hooks/useStakedList';
import { useVirtualBalanceContext } from '../../../context/VirtualBalanceContext';

interface props {
  onClose: () => void;
}

export interface DepositType {
  amount: bigint;
  end: bigint;
  shareAmount: bigint;
  start: bigint;
}

const UnstakeXVirtualForm: React.FC<props> = ({ onClose }) => {
  const { user } = usePrivy();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [convertData, setConvertData] = useState<`0x${string}` | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedPool, setSelectedPool] = useState<number | null>();
  const { data } = useGetCoinData();
  const { stakedList } = useStakedList();

  const intervals = {
    y: 31536000,
    M: 2592000,
    w: 604800,
    d: 86400,
    h: 3600,
    m: 60,
  };

  const { xVirtualBalance, fetchXVirtualBalance, fetchVirtualBalance } =
    useVirtualBalanceContext();

  const { writeContractAsync } = useWriteContract({
    mutation: {
      onSuccess(data) {
        // On success, wait for tx approved before closing modal
        enqueueSnackbar({
          key: data,
          message: `Transaction is in progress`,
          action: (
            <a
              rel="noreferrer"
              target="_blank"
              href={`${activeChain.blockExplorers.default.url}/tx/${data}`}
            >
              <Button>View Tx</Button>
            </a>
          ),
        });

        setConvertData(data);
      },
      onError(error) {
        const errorMessage = renderContractError(error);
        setErrorMessage(errorMessage);
      },
    },
  });

  const { isSuccess: isTxConfirmed, isLoading: isTxConfirming } =
    useWaitForTransactionReceipt({ hash: convertData });

  const unstake = async () => {
    await writeContractAsync({
      chainId: activeChain.id,
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'withdraw',
      args: [selectedPool as any, user?.wallet?.address as `0x${string}`],
    })
      .then(async (txnHash) => {
        const txReceipt = await waitForTransactionReceipt(config, {
          chainId: activeChain.id,
          hash: txnHash,
        });

        if (txReceipt.status === 'success') {
          enqueueSnackbar({
            message: 'Converted successfully',
            variant: 'success',
          });
        } else {
          enqueueSnackbar({
            message: 'Transaction failed',
            variant: 'error',
          });
          setIsLoading(false);
          return;
        }
        await fetchVirtualBalance();
        await fetchXVirtualBalance();
        onClose();
      })
      .catch(async (e: any) => {
        setIsLoading(false);
      });
  };

  const submitForm = async () => {
    try {
      setErrorMessage('');
      if ((selectedPool ?? -1) < 0) return;

      if (!stakedList[selectedPool as number])
        return setErrorMessage('Please select a pool to unstake.');

      if (xVirtualBalance < stakedList[selectedPool as number].shareAmount) {
        enqueueSnackbar({
          message:
            'You have used xVIRTUAL to stake, please unstake them before conversion.',
          variant: 'error',
        });

        setErrorMessage(
          'You have used xVIRTUAL to stake, please unstake them before conversion.'
        );

        setIsLoading(false);
        return;
      }
      setIsLoading(true);

      await checkNetwork();

      await unstake();
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage = renderContractError(error);
      setErrorMessage(errorMessage);
      throw new Error(error);
    }
  };

  function formatTime(endTimestamp: number) {
    const currentTimestamp = Math.floor(new Date().getTime() / 1000);
    const diff = currentTimestamp - Number(endTimestamp);

    if (diff > 0) {
      return 'Released';
    }
    const timeText = diff < 0 ? 'left' : 'ago';
    const absoluteDiff = Math.abs(diff);

    if (absoluteDiff < 5) {
      return 'Released';
    }

    for (const [unit, seconds] of Object.entries(intervals)) {
      const intervalCount = Math.floor(absoluteDiff / Number(seconds));

      if (intervalCount >= 1) {
        return `${intervalCount}${unit}${' '} ${timeText}`;
      }
    }

    return 'Released';
  }

  const handleSetSelectedPool = (index: number) => {
    if (index === selectedPool) return setSelectedPool(null);
    setSelectedPool(index);
  };

  return (
    <Stack p={3}>
      <Stack flex={1} justifyContent="space-between">
        <Stack mb={2} gap={1} maxHeight={'75vh'} overflow={'auto'}>
          {stakedList.length === 0 && (
            <Typography variant="body1">Nothing found.</Typography>
          )}
          {stakedList.map((stake, index) => {
            let timelockText = formatTime(Number(stake.end));

            const inLockPeriod = timelockText !== 'Released';
            return (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                flexDirection={'column'}
                p={2}
                border={1}
                borderRadius={'8px'}
                borderColor={selectedPool === index ? '#43BDCE' : '#DDDDDD'}
                /* sx={{
                  cursor: inLockPeriod ? 'auto' : 'pointer',
                  opacity: inLockPeriod ? 0.5 : 1, // Reduce opacity for visual indication
                }} */
                onClick={() => {
                  /*   if (!inLockPeriod) {
                    handleSetSelectedPool(index);
                  } */
                  handleSetSelectedPool(index);
                }}
              >
                <Stack
                  display={'flex'}
                  justifyContent={'space-between'}
                  flexDirection={'row'}
                  alignItems={'center'}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {formatNumber(
                      Number(ethers.formatEther(stake.shareAmount)),
                      2,
                      2
                    )}{' '}
                    $xVIRTUAL
                  </Typography>
                  <Typography variant="body2">Unlock</Typography>
                </Stack>
                <Stack
                  display={'flex'}
                  justifyContent={'space-between'}
                  flexDirection={'row'}
                  alignItems={'center'}
                >
                  <Typography variant="body2">
                    {formatNumber(
                      Number(ethers.formatEther(stake.amount)),
                      2,
                      2
                    )}{' '}
                    $VIRTUAL
                  </Typography>

                  {/* <Typography variant="body2" fontWeight={700}>
                    {timelockText}
                  </Typography> */}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Stack>
      {stakedList.length > 0 && (
        <LoadingButton
          disabled={(selectedPool ?? -1) < 0}
          loading={isLoading || isTxConfirming}
          sx={virtualDetailModalStyle.btnStake}
          size="large"
          variant="contained"
          onClick={submitForm}
          fullWidth
        >
          Convert
        </LoadingButton>
      )}
      {errorMessage && errorMessage.length > 0 && (
        <Typography variant="body2" color="error" mt={1}>
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};

export default UnstakeXVirtualForm;
